import React from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import {
  TrendingUpIcon,
  TrendingDownIcon,
  MinusIcon,
} from "@heroicons/react/outline";

const StatCard = ({
  title,
  value,
  previousValue,
  format = "number",
  icon: Icon,
  color = "primary",
  loading = false,
}) => {
  const formatValue = (val) => {
    if (format === "percentage") {
      return `${val}%`;
    } else if (format === "currency") {
      return `$${val.toLocaleString()}`;
    }
    return val.toLocaleString();
  };

  const calculateChange = () => {
    if (!previousValue || previousValue === 0) return 0;
    return ((value - previousValue) / previousValue) * 100;
  };

  const change = calculateChange();
  const isPositive = change > 0;
  const isNeutral = change === 0;

  const colorClasses = {
    primary: "bg-primary/10 dark:bg-primary/20 text-primary",
    success: "bg-success/10 dark:bg-success/20 text-success",
    warning: "bg-warning/10 dark:bg-warning/20 text-warning",
    danger: "bg-danger/10 dark:bg-danger/20 text-danger",
    info: "bg-info/10 dark:bg-info/20 text-info",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-xl shadow-sm bg-white dark:bg-gray-800 p-6 hover:shadow-md transition-shadow duration-200"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {Icon && <Icon className="w-6 h-6" />}
        </div>
        {previousValue !== undefined && (
          <div
            className={`flex items-center text-sm ${
              isPositive
                ? "text-success"
                : isNeutral
                ? "text-gray-500"
                : "text-danger"
            }`}
          >
            {isPositive ? (
              <TrendingUpIcon className="w-4 h-4 mr-1" />
            ) : isNeutral ? (
              <MinusIcon className="w-4 h-4 mr-1" />
            ) : (
              <TrendingDownIcon className="w-4 h-4 mr-1" />
            )}
            <span>{Math.abs(change).toFixed(1)}%</span>
          </div>
        )}
      </div>

      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
        {title}
      </h3>

      {loading ? (
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      ) : (
        <div className="text-2xl font-bold text-gray-900 dark:text-white">
          {format === "percentage" ? (
            <CountUp
              start={0}
              end={Number.isFinite(value) ? value : 0}
              duration={2}
              decimals={1}
              suffix="%"
            />
          ) : (
            <CountUp
              start={0}
              end={Number.isFinite(value) ? value : 0}
              duration={2}
              separator=","
              prefix={format === "currency" ? "$" : ""}
            />
          )}
        </div>
      )}
    </motion.div>
  );
};

export default StatCard;
